import type { CellCoords } from 'handsontable/base';
import { SelectionController } from 'handsontable/selection';

import { CELL_CHECKBOX_RENDERER_CLASS_NAME } from '../cellTypes/cellTypeConstants';
import { getTableElementFromHotInstance } from '../utils/domUtils/hotTableDomAccessorUtils';
import { getFirstSelection, getStartCol, getStartRow } from '../../bridge/utils/selectionUtils/tableSelectionAccessors';
import {
    hasSelections,
    isSelectingSingleCell,
    isSelectingSingleSelection,
} from '../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import { findAnchorElement } from '../../../../utils/dom/domEventTargetUtils';
import { hasCommandModifier, isControlKeyCommand } from '../../../../utils/keyboard/keyboardUtility';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';
import TouchCellSelectionController from './TouchCellSelectionController';

export const POINTER_INTERACTION_PLUGIN_NAME = 'PointerInteraction';
export const POINTER_INTERACTION_PLUGIN_PRIORITY = 2;

type HotMouseEvent = MouseEvent & {
    // Handsontable private property
    isImmediatePropagationEnabled?: boolean;
};

/**
 * Handles Milanote-specific table interaction behaviour before Handsontable updates selection.
 */
class PointerInteractionHotPlugin extends MilanoteHotPlugin {
    private touchCellSelectionController = new TouchCellSelectionController(this.milanoteHot);

    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component.
     */
    static get PLUGIN_KEY(): string {
        return POINTER_INTERACTION_PLUGIN_NAME;
    }

    static get PLUGIN_PRIORITY(): number {
        return POINTER_INTERACTION_PLUGIN_PRIORITY;
    }

    enablePlugin(): void {
        this.addHook('afterInit', () => this.afterInit());
        this.addHook('beforeOnCellMouseDown', (...args) => this.onBeforeOnCellMouseDown(...args));
        this.addHook('afterSelection', (...args) => this.onAfterSelection(...args));
        this.addHook('afterSelectionFocusSet', (...args) => this.onAfterSelectionFocusSet(...args));

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    /**
     * Applies Milanote mouse and viewport behaviour after Handsontable has registered its event listeners.
     */
    afterInit(): void {
        this.suppressMouseDownHandlingForTableUtils();
        this.suppressScrollViewport();
    }

    /**
     * Prevents Handsontable from scrolling its internal viewport after a cell range changes.
     */
    onAfterSelection(
        _row: number,
        _column: number,
        _row2: number,
        _column2: number,
        preventScrolling: { value: boolean },
        _selectionLayerLevel: number,
    ): void {
        preventScrolling.value = true;
    }

    /**
     * Prevents Handsontable from scrolling its internal viewport after the focused cell changes.
     */
    onAfterSelectionFocusSet(_row: number, _column: number, preventScrolling: { value: boolean }): void {
        preventScrolling.value = true;
    }

    /**
     * Intercepts cell mouse-down interactions so embedded controls, element selection, multi-select shortcuts, and
     * single-cell re-entry into edit mode all behave like the surrounding Milanote selection model expects.
     */
    onBeforeOnCellMouseDown(
        event: MouseEvent,
        coords: CellCoords,
        _TD: HTMLTableCellElement,
        _controller: SelectionController,
    ): false | void {
        const hotEvent = event as HotMouseEvent;
        if (hotEvent.isImmediatePropagationEnabled === false) return;

        const {
            isReadOnly: isElementReadOnly,
            isSingleSelected: isElementSingleSelected,
            deselectElement,
        } = this.milanoteHot.milanoteProps;

        // Read-only tables should keep their existing selection state and avoid entering edit interactions.
        if (isElementReadOnly) {
            event.stopImmediatePropagation();
            return;
        }

        const target = event.target as HTMLElement | null;

        // If user clicks on a checkbox in a cell, toggle the checkbox without selecting the cell.
        const isTargetACellCheckbox = !!target?.closest(`.${CELL_CHECKBOX_RENDERER_CLASS_NAME}`);
        if (isTargetACellCheckbox) {
            hotEvent.isImmediatePropagationEnabled = false;
        }

        // If a link has been clicked, interact with the link without selecting the cell.
        const isTargetACellLink = !!findAnchorElement(target);
        if (isTargetACellLink) {
            event.stopImmediatePropagation();
            event.preventDefault();
            return false;
        }

        // A table element must be selected before Handsontable can own cell-level mouse interactions.
        if (!isElementSingleSelected) {
            this.selectTableElement(event);
            return;
        }

        // Right-clicks are reserved for context-menu handling and should not trigger edit re-entry.
        const isRightMouseClick = event.button === 2;
        if (isRightMouseClick) return;

        const hotCurrentCellSelections = this.milanoteHot.getSelected();
        const currentCellSelection = getFirstSelection(hotCurrentCellSelections);

        const isModifierClick = hasCommandModifier(event) || isControlKeyCommand(event);
        if (isModifierClick) {
            // Modifier-click on a table without cell selections should deselect the Table element
            // instead of selecting a cell.
            if (!currentCellSelection) {
                event.stopImmediatePropagation();
                event.preventDefault();

                deselectElement();
            }

            return;
        }

        const isSelectingCurrentlySelectedCell =
            !!hotCurrentCellSelections &&
            !!currentCellSelection &&
            isSelectingSingleSelection(hotCurrentCellSelections) &&
            isSelectingSingleCell(currentCellSelection) &&
            coords.row === getStartRow(currentCellSelection) &&
            coords.col === getStartCol(currentCellSelection);

        const activeEditor = this.milanoteHot.getActiveEditor();
        if (!activeEditor) return;

        if (!isSelectingCurrentlySelectedCell) {
            // Defer different-cell touch selection until it resolves as a tap, so a pan keeps the current cell selected.
            if (event.type === 'touchstart') {
                // Handsontable types this hook event as MouseEvent, but can call it with a touchstart TouchEvent on touch devices.
                this.touchCellSelectionController.begin(event as unknown as TouchEvent, coords);
            }

            return;
        }

        event.stopImmediatePropagation();
        event.preventDefault();

        activeEditor.beginEditing(undefined, event);
    }

    disablePlugin(): void {
        this.touchCellSelectionController.dispose();
        super.disablePlugin();
    }

    /**
     * Selects the table element when a cell is clicked before the table itself is selected.
     */
    selectTableElement(event: MouseEvent): void {
        const tableElement = getTableElementFromHotInstance(this.milanoteHot);
        if (!tableElement) return;

        // Both events are necessary to trigger the selection of the table element
        // In ElementContainer mouseDown alone will never select the element as there is an early
        // return for document mode, and click will only run if there has been a mouseDown event
        tableElement.dispatchEvent(new MouseEvent('mousedown', event));
        tableElement.click();
    }

    /**
     * Prevents the table from losing focus when an external click is made on a DOM element
     * with data-table-util attribute
     */
    suppressMouseDownHandlingForTableUtils(): void {
        // Find the existing event handler for table utils (added by handsontable).
        // We will wrap it with some extra checks and re-add it.
        const mouseDownEvent = this.milanoteHot.eventListeners?.find(
            ({ event, element }) => event === 'mousedown' && element.nodeName === 'HTML',
        );

        if (!mouseDownEvent) return;

        const { eventManager, element, event: eventName, callback } = mouseDownEvent;

        eventManager.removeEventListener(element, eventName, callback);
        eventManager.addEventListener(element, eventName, (event: Event) => {
            const cellSelections = this.milanoteHot.getSelected();
            if (!cellSelections || !hasSelections(cellSelections)) return;

            const target = event.target as HTMLElement | null;
            if (target?.closest('[data-table-util]')) return;

            callback(event);
        });
    }

    /**
     * Prevents direct Handsontable viewport scrolling, including when its editor opens.
     *
     * The `preventScrolling.value = true` in the selection hooks above only suppress Handsontable 17's internal
     * viewport scroller while a selection changes.
     *
     * Handsontable also calls `view.scrollViewport` directly when beginning cell editing and through
     * explicit scroll APIs, so this override keeps those paths from repositioning the table viewport as well.
     */
    suppressScrollViewport(): void {
        this.milanoteHot.view.scrollViewport = () => {
            // Do nothing
        };

        this.milanoteHot.view.scrollViewportHorizontally = () => {
            // Do nothing
        };

        this.milanoteHot.view.scrollViewportVertically = () => {
            // Do nothing
        };
    }
}

export default PointerInteractionHotPlugin;
